import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import StickyModule from './StickyNote';
import LeadsModule from './LeadsModule';
import OrderModule from './OrderModule';
import RemindersModule from './RemindersModule';
import DailyWrapModule from './DailyWrapModule';
import ChatModule from './ChatModule';
import TargetModule from './TargetModule';
import { toast } from 'sonner';

const DEFAULTS = {
  leads: { x: 24, y: 28 },
  orders: { x: 700, y: 44 },
  reminders: { x: 1090, y: 20 },
  wrapup: { x: 1460, y: 36 },
  chat: { x: 1090, y: 560 },
  target: { x: 700, y: 580 },
};

const DEFAULT_COLORS = { leads: 'yellow', orders: 'blue', reminders: 'pink', wrapup: 'mint', chat: 'purple', target: 'apricot' };

let chimeCtx = null;
const playChime = () => {
  try {
    chimeCtx = chimeCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (chimeCtx.state === 'suspended') chimeCtx.resume();
    const t0 = chimeCtx.currentTime;
    [659.25, 987.77].forEach((f, i) => {
      const o = chimeCtx.createOscillator();
      const g = chimeCtx.createGain();
      o.type = 'sine';
      o.frequency.value = f;
      const t = t0 + i * 0.15;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.16, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
      o.connect(g).connect(chimeCtx.destination);
      o.start(t);
      o.stop(t + 1);
    });
  } catch {}
};

export default function AgentBoard({ agent, sendActivity }) {
  const [positions, setPositions] = useState(DEFAULTS);
  const [noteColors, setNoteColors] = useState(DEFAULT_COLORS);
  const [leads, setLeads] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1280);

  useEffect(() => {
    api.get(`/chat/${agent.id}`).then((r) => setChatMessages(r.data));
    const handler = (e) => {
      if (e.detail.agent_id !== agent.id) return;
      const msg = e.detail.message;
      setChatMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      if (msg.sender === 'admin') toast(`📌 New note from Admin: ${msg.text?.slice(0, 60) || 'attachment'}`);
    };
    window.addEventListener('snd-chat', handler);
    return () => window.removeEventListener('snd-chat', handler);
  }, [agent.id]);

  const sendChat = async (text, attachment) => {
    const r = await api.post(`/chat/${agent.id}`, {
      sender: 'agent', sender_name: agent.name, text, attachment: attachment || null,
    });
    setChatMessages((prev) => (prev.some((m) => m.id === r.data.id) ? prev : [...prev, r.data]));
    sendActivity('Replied to Admin chat');
  };

  const unreadChat = chatMessages.filter((m) => m.sender === 'admin' && !m.read_by_agent).length;

  const markChatRead = () => {
    if (!unreadChat) return;
    api.post(`/chat/${agent.id}/read?side=agent`);
    setChatMessages((prev) => prev.map((m) => ({ ...m, read_by_agent: true })));
  };

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1280);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const knownLeadIds = useRef(null);
  const loadLeads = useCallback(() => api.get(`/leads?agent_id=${agent.id}`).then((r) => {
    const data = r.data;
    if (knownLeadIds.current) {
      const fresh = data.filter((l) => !knownLeadIds.current.has(l.id) && !l.archived && l.status !== 'Closed');
      if (fresh.length > 0) {
        playChime();
        toast.success(fresh.length === 1
          ? `🔔 New lead: ${fresh[0].customer_name} landed on your board!`
          : `🔔 ${fresh.length} new leads landed on your board!`);
      }
    }
    knownLeadIds.current = new Set(data.map((l) => l.id));
    setLeads(data);
  }), [agent.id]);
  const loadReminders = useCallback(() => api.get(`/reminders?agent_id=${agent.id}`).then((r) => setReminders(r.data)), [agent.id]);
  const loadOrders = useCallback(() => api.get(`/orders?agent_id=${agent.id}`).then((r) => setOrders(r.data)), [agent.id]);

  useEffect(() => {
    loadLeads();
    loadReminders();
    loadOrders();
    api.get(`/board-layout/${agent.id}`).then((r) => {
      setPositions({ ...DEFAULTS, ...(r.data.positions || {}) });
      setNoteColors({ ...DEFAULT_COLORS, ...(r.data.colors || {}) });
    });
    const poll = setInterval(() => { loadLeads(); loadReminders(); loadOrders(); }, 30000);
    const onNewLead = () => loadLeads();
    window.addEventListener('snd-leads-refresh', onNewLead);
    return () => {
      clearInterval(poll);
      window.removeEventListener('snd-leads-refresh', onNewLead);
    };
  }, [agent.id, loadLeads, loadReminders, loadOrders]);

  const onDragStop = (id, pos) => {
    const next = { ...positions, [id]: pos };
    setPositions(next);
    api.put(`/board-layout/${agent.id}`, { positions: next, colors: noteColors });
    sendActivity(`Rearranged ${id} note`);
  };

  const onColorChange = (id, colorKey) => {
    const next = { ...noteColors, [id]: colorKey };
    setNoteColors(next);
    api.put(`/board-layout/${agent.id}`, { positions, colors: next });
    sendActivity(`Recolored ${id} note to ${colorKey}`);
  };

  const overdue = reminders.filter((r) => !r.done && r.due_at && new Date(r.due_at) < new Date());

  const modules = [
    {
      id: 'leads', title: 'Leads Board', subtitle: 'your biggest note', color: noteColors.leads, rotate: -0.6, width: 640,
      badge: <span className="font-hand text-lg font-bold px-2 rounded-full bg-yellow-300/70 text-yellow-900">{leads.length}</span>,
      el: <LeadsModule agent={agent} leads={leads} refresh={loadLeads} sendActivity={sendActivity} />,
    },
    {
      id: 'orders', title: 'Create Order', subtitle: 'syncs to WooCommerce', color: noteColors.orders, rotate: 1, width: 360,
      badge: <span className="font-hand text-lg font-bold px-2 rounded-full bg-sky-200 text-sky-900">{orders.length}</span>,
      el: <OrderModule agent={agent} orders={orders} refresh={loadOrders} sendActivity={sendActivity} />,
    },
    {
      id: 'reminders', title: 'Reminders', subtitle: "don't forget!", color: noteColors.reminders, rotate: -1.4, width: 350,
      badge: (
        <span className={`font-hand text-lg font-bold px-2 rounded-full ${overdue.length ? 'bg-red-500 text-white' : 'bg-pink-200 text-pink-900'}`} data-testid="reminders-badge">
          {overdue.length ? `${overdue.length}!` : reminders.filter((r) => !r.done).length}
        </span>
      ),
      el: <RemindersModule agent={agent} reminders={reminders} refresh={loadReminders} sendActivity={sendActivity} />,
    },
    {
      id: 'wrapup', title: 'Daily Wrap-Up', subtitle: 'how did today go?', color: noteColors.wrapup, rotate: 0.8, width: 330,
      badge: <span className="font-hand text-lg font-bold px-2 rounded-full bg-emerald-200 text-emerald-900">🌙</span>,
      el: <DailyWrapModule agent={agent} />,
    },
    {
      id: 'target', title: 'Target Meter', subtitle: 'brownie points on the line!', color: noteColors.target, rotate: 1.2, width: 340,
      badge: <span className="font-hand text-lg font-bold px-2 rounded-full bg-orange-200 text-orange-900">🎯</span>,
      el: <TargetModule agent={agent} />,
    },
    ...(chatMessages.length > 0 ? [{
      id: 'chat', title: 'Admin Chat', subtitle: 'a note from HQ', color: noteColors.chat, rotate: -1, width: 350,
      badge: unreadChat > 0
        ? <span data-testid="chat-unread-badge" className="font-hand text-lg font-bold px-2 rounded-full bg-red-500 text-white animate-pulse">{unreadChat} new</span>
        : <span className="font-hand text-lg font-bold px-2 rounded-full bg-purple-200 text-purple-900">{chatMessages.length}</span>,
      el: <ChatModule agent={agent} messages={chatMessages} onSend={sendChat} onRead={markChatRead} />,
    }] : []),
  ];

  return (
    <main className="corkboard min-h-[calc(100vh-64px)]" data-testid="agent-artboard">
      <div className="px-5 md:px-8 pt-5 flex items-baseline gap-3 flex-wrap">
        <h2 className="font-display font-extrabold text-2xl text-slate-900">{agent.name.split(' ')[0]}'s Artboard</h2>
        <p className="font-hand text-xl text-slate-500">drag your notes anywhere ✏️</p>
        {overdue.length > 0 && (
          <span data-testid="overdue-alert" className="font-hand text-lg font-bold bg-red-500 text-white px-3 py-0.5 rounded-full rotate-[-1deg] sticky-shadow animate-pulse">
            ⚠️ {overdue.length} overdue follow-up{overdue.length > 1 ? 's' : ''}!
          </span>
        )}
      </div>
      {isDesktop ? (
        <div className="relative overflow-x-auto">
          <div className="relative min-h-[1060px]" style={{ minWidth: 1810 }} data-testid="artboard-canvas">
            {modules.map((m) => (
              <StickyModule key={m.id} {...m} draggable position={positions[m.id]} onStop={onDragStop} onColorChange={onColorChange}>
                {m.el}
              </StickyModule>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-8 p-4 pt-6 max-w-2xl mx-auto pb-16">
          {modules.map((m) => (
            <StickyModule key={m.id} {...m} draggable={false} onColorChange={onColorChange}>
              {m.el}
            </StickyModule>
          ))}
        </div>
      )}
    </main>
  );
}
